'use client';

import { useEffect, useMemo, useState } from 'react';
import { Activity, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { fetchUiState } from '@/lib/uiState';

function getStatusColor(status = '') {
  const normalized = status.toLowerCase();
  if (normalized === 'failed') return 'text-red-500';
  if (normalized === 'completed') return 'text-emerald-500';
  return 'text-amber-500';
}

function formatLabel(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleTimeString();
  } catch {
    return value;
  }
}

function progressToPercent(progress) {
  if (typeof progress !== 'number') return 0;
  return Math.round(Math.max(0, Math.min(progress, 1)) * 100);
}

export function AdminJobStatusIndicator({ isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], active: [], global: [] });

  useEffect(() => {
    if (!isAdmin) return undefined;

    let mounted = true;
    let timer;

    const fetchState = async () => {
      if (!mounted) return;
      setLoading(true);
      try {
        const res = await fetchUiState();
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const jobs = json?.data?.jobs || {};
        if (!mounted) return;
        setData({
          items: Array.isArray(jobs.items) ? jobs.items : [],
          active: Array.isArray(jobs.active) ? jobs.active : [],
          global: Array.isArray(jobs.global) ? jobs.global : [],
        });
        setError(null);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load job status');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchState();
    timer = setInterval(fetchState, 15000);

    const onRefreshRequest = () => fetchState();
    window.addEventListener('ui-jobs-refresh', onRefreshRequest);

    return () => {
      mounted = false;
      clearInterval(timer);
      window.removeEventListener('ui-jobs-refresh', onRefreshRequest);
    };
  }, [isAdmin]);

  const recentJobs = useMemo(() => data.items.slice(0, 5), [data.items]);
  const activeCount = data.active.length;
  const hasContent = activeCount > 0 || recentJobs.length > 0 || error;

  if (!isAdmin || !hasContent) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          mode="icon"
          shape="circle"
          className="relative size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
          title="Admin Jobs"
          aria-label="Admin job status"
        >
          {loading ? (
            <Loader2 className="size-4.5 animate-spin" />
          ) : (
            <Activity className={cn('size-4.5', activeCount > 0 ? 'text-amber-500' : 'text-muted-foreground/70')} />
          )}
          {activeCount > 0 ? (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
              {activeCount > 9 ? '9+' : String(activeCount)}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Admin Jobs</p>
              <p className="text-xs text-muted-foreground">
                {activeCount > 0 ? `${activeCount} aktiv` : 'Keine aktiven Jobs'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                try {
                  const ev = new CustomEvent('ui-jobs-refresh');
                  window.dispatchEvent(ev);
                } catch {}
              }}
              className="h-7 px-2 text-xs"
            >
              Aktualisieren
            </Button>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}

          <div className="space-y-2 max-h-72 overflow-auto pr-1">
            {recentJobs.length === 0 ? (
              <p className="text-xs text-muted-foreground">Keine Jobs in der Historie.</p>
            ) : (
              recentJobs.map((job) => {
                const percent = progressToPercent(job.progress);
                return (
                  <div key={job.jobId} className="rounded-md border border-border/60 bg-muted/40 p-3">
                    <div className="flex items-center justify-between text-xs font-semibold text-foreground">
                      <span className="capitalize">{job.type.replace(/-/g, ' ')}</span>
                      <span className={cn('capitalize', getStatusColor(job.status))}>{job.status}</span>
                    </div>
                    {job.message ? (
                      <p className="mt-1 text-xs text-muted-foreground">{job.message}</p>
                    ) : null}
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          job.status === 'completed'
                            ? 'bg-emerald-500'
                            : job.status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-amber-500',
                        )}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{formatLabel(job.updatedAt)}</span>
                      <span>#{job.jobId.slice(-6)}</span>
                    </div>
                    {(() => {
                      const orphanCount =
                        typeof job.result?.orphansTotal === 'number'
                          ? job.result.orphansTotal
                          : job.result?.orphans;
                      if (typeof orphanCount !== 'number') return null;
                      return (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Orphans: {orphanCount}
                        </p>
                      );
                    })()}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AdminJobStatusIndicator;
